import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { clsx } from 'clsx';
import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import type { SLBPosition } from '@/types';

export const STORAGE_KEY = 'inventory_col_max_widths';
export const DEFAULT_WIDTH = 120;
export const ROW_NUMBER_DEFAULT_WIDTH = 40;
const ROW_HEIGHT = 38;
export const HEADER_ROW_HEIGHT_PX = 56;
const SKELETON_BAR_FRACTION = 0.7;
/** Gap between columns (mx-1.5 = 6+6). Must match InventoryTable so pinned section widths align. */
const COL_GAP_PX = 12;

const FILTERABLE_COLUMN_IDS = new Set([
  'accountId',
  'counterparty',
  'currency',
  'exchange',
  'caStatus',
  'quantity',
  'settledQuantity',
  'quantityT1',
  'quantityT2',
  'quantityTx',
  'leverageRate',
  'valueEUR',
  'benchmarkFee',
  'activeUtilisation',
  'quantityOnLoan',
  'rebateRate',
  'currencyBenchmark',
  'dailyPotentialEUR',
  'adtv30D',
  'adtvMultiple',
  'adtvAdjustedPotentialEUR',
  'lentToCounterparties',
  'l2cpQuantity',
  'l2cpAmountEur',
  'l2cpRate',
  'l2cpGrossRate',
  'l2cpSnpDelta',
  'pendingTransfers',
  'cimbEq2Quantity',
  'cimbSfQuantity',
  'cimbSf3Quantity',
  'velocityLQuantity',
  'velocityMQuantity',
  'exDate',
]);

const FILTER_ICON_EXTRA_PX = 24;

// eslint-disable-next-line react-refresh/only-export-components -- storage helper paired with the skeleton component; splitting needs ~4 import-site updates
export const getStoredWidths = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
};

// eslint-disable-next-line react-refresh/only-export-components -- storage helper paired with the skeleton component; splitting needs ~4 import-site updates
export const storeColumnWidths = (widths: Record<string, number>): void => {
  try {
    const existing = getStoredWidths();
    const merged = { ...existing };
    for (const [key, value] of Object.entries(widths)) {
      merged[key] = Math.max(merged[key] ?? 0, value);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // ignore
  }
};

type SkeletonColumnDef = ColumnDef<SLBPosition, unknown>;

const getColId = (col: SkeletonColumnDef): string => getColumnId(col);

const getColWidth = (
  col: SkeletonColumnDef,
  storedWidths: Record<string, number>
): number => {
  const cid = getColId(col);
  const defaultW = cid === 'rowNumber' ? ROW_NUMBER_DEFAULT_WIDTH : DEFAULT_WIDTH;
  const base = storedWidths[cid] ?? defaultW;
  return base + (FILTERABLE_COLUMN_IDS.has(cid) ? FILTER_ICON_EXTRA_PX : 0);
};

const getHeaderText = (col: SkeletonColumnDef): string => {
  const raw = col.header;
  if (raw === undefined || raw === null) return 'Column';
  if (typeof raw === 'function') {
    const node = (raw as () => React.ReactNode)();
    if (typeof node === 'string') return node;
    return '';
  }
  return String(raw);
};

type TableSkeletonProps = {
  columns: SkeletonColumnDef[];
  pinnedLeft?: string[];
  pinnedRight?: string[];
};

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ columns, pinnedLeft = [], pinnedRight = [] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rowCount, setRowCount] = useState(30);
  const storedWidths = useMemo(getStoredWidths, []);
  const columnWidths = useMemo(
    () => columns.map((col) => getColWidth(col, storedWidths)),
    [columns, storedWidths]
  );
  const { leftCols, centerCols, rightCols, leftWidth, rightWidth } = useMemo(() => {
    const pinnedLeftSet = new Set(pinnedLeft);
    const pinnedRightSet = new Set(pinnedRight);
    const left: typeof columns = [];
    const center: typeof columns = [];
    const right: typeof columns = [];
    columns.forEach((col, _i) => {
      const cid = getColId(col);
      if (pinnedLeftSet.has(cid)) left.push(col);
      else if (pinnedRightSet.has(cid)) right.push(col);
      else center.push(col);
    });
    let lw = 0;
    left.forEach((col) => {
      const w = getColWidth(col, storedWidths);
      lw += w + COL_GAP_PX;
    });
    let rw = 0;
    right.forEach((col) => {
      const w = getColWidth(col, storedWidths);
      rw += w + COL_GAP_PX;
    });
    return { leftCols: left, centerCols: center, rightCols: right, leftWidth: lw, rightWidth: rw + (right.length > 0 ? 16 : 0) };
  }, [columns, pinnedLeft, pinnedRight, storedWidths]);
  const hasPinnedLeft = leftCols.length > 0;
  const hasPinnedRight = rightCols.length > 0;
  const colIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    columns.forEach((col, i) => m.set(getColId(col), i));
    return m;
  }, [columns]);
  const getWidthForCol = (col: (typeof columns)[number]): number => columnWidths[colIndexMap.get(getColId(col)) ?? 0];
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const available = el.clientHeight - HEADER_ROW_HEIGHT_PX;
    setRowCount(Math.max(1, Math.ceil(available / ROW_HEIGHT)));
  }, []);
  const renderHeaderGroup = (cols: typeof columns): React.ReactNode =>
    cols.map((col) => {
      const w = getWidthForCol(col);
      return (
        <div
          key={getColId(col)}
          className="mx-1.5 flex min-h-0 flex-shrink-0 items-center line-clamp-2 break-words"
          style={{ width: `${w}px`, minWidth: `${w}px` }}
          title={getHeaderText(col)}
        >
          {getHeaderText(col)}
        </div>
      );
    });
  const renderBodyGroup = (cols: typeof columns): React.ReactNode =>
    cols.map((col) => {
      const w = getWidthForCol(col);
      const barWidth = Math.round(w * SKELETON_BAR_FRACTION);
      return (
        <div
          key={getColId(col)}
          className="mx-1.5 flex flex-shrink-0 items-center"
          style={{ width: `${w}px`, minWidth: `${w}px` }}
        >
          <div
            className="h-3.5 flex-shrink-0 animate-pulse rounded bg-gray-200"
            style={{ width: `${barWidth}px` }}
          />
        </div>
      );
    });
  return (
    <div ref={containerRef} className="flex flex-1 flex-col overflow-auto">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        <div
          className="sticky top-0 z-10 flex shrink-0 border-b border-gray-200 bg-gray-50 py-2 text-sm font-medium text-gray-900"
          style={{ height: `${HEADER_ROW_HEIGHT_PX}px`, minHeight: `${HEADER_ROW_HEIGHT_PX}px` }}
        >
          <div className="flex items-center">
          {hasPinnedLeft && (
            <div
              className="sticky left-0 z-[12] flex shrink-0 items-center self-stretch bg-gray-50 pl-4"
              style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
            >
              {renderHeaderGroup(leftCols)}
            </div>
          )}
          <div className={clsx('flex items-center', !hasPinnedLeft && 'pl-4', !hasPinnedRight && 'pr-4')}>
            {renderHeaderGroup(centerCols)}
          </div>
          {hasPinnedRight && (
            <div
              className="sticky right-0 z-[12] flex shrink-0 items-center self-stretch bg-gray-50 pr-7"
              style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
            >
              {renderHeaderGroup(rightCols)}
            </div>
          )}
          </div>
        </div>
        <div className="flex-1">
          {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="flex items-center border-b border-gray-200 py-2"
            style={{ height: `${ROW_HEIGHT}px` }}
          >
            {hasPinnedLeft && (
              <div
                className="sticky left-0 z-[2] flex shrink-0 items-center self-stretch bg-white pl-4"
                style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
              >
                {renderBodyGroup(leftCols)}
              </div>
            )}
            <div className={clsx('flex items-center', !hasPinnedLeft && 'pl-4', !hasPinnedRight && 'pr-4')}>
              {renderBodyGroup(centerCols)}
            </div>
            {hasPinnedRight && (
              <div
                className="sticky right-0 z-[2] flex shrink-0 items-center self-stretch bg-white pr-7"
                style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
              >
                {renderBodyGroup(rightCols)}
              </div>
            )}
          </div>
          ))}
        </div>
      </div>
    </div>
  );
};
